/* eslint-disable */
// ============================================================
//  App.jsx - Routeur principal HorseConnex
// ============================================================

import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { applyTheme, getCurrentTheme, NIGHT_BG } from "./theme";
import { supabase, getBlockedIds, trackAppOpen, trackVisitorSource, trackVisitorCountry, getMyPros, getProById, unsubscribeProByKey, getAppSettings, findUnclaimedProForMe, claimProByEmail } from "./supabase";
import {
  T,
  loadLang,
  LOGO,
  ADMIN_EMAIL,
  MAINTENANCE_MODE,
  needsProfileUpdate,
  PRO_METIERS,
} from "./constants";
import { useLang, getDisplayName } from "./utils";
import { runBackHandlers } from "./back";
import { G, Toast, InstallAppPopup, CookieBanner } from "./ui";
import EvolvedNotice from "./EvolvedNotice";
import FounderNotice from "./FounderNotice";

// Lancement de l'offre « membre fondateur ». Les comptes anterieurs sont remercies
// d'etre la depuis le debut ; les suivants sont accueillis.
const FOUNDER_OFFER_LAUNCH = new Date("2026-09-09T00:00:00Z");
import CreateFicheNotice from "./CreateFicheNotice";
import { initNativePush } from "./native-push";
import { initAppUpdate } from "./native-update";
import { initNativeBack } from "./native-back";
// === Eager (chargés au démarrage : flow d'auth et navigation core) ===
import {
  SplashScreen,
  LangScreen,
  LegalScreen,
  AuthChoice,
  LoginScreen,
  RegisterScreen,
  ResetPasswordScreen,
  LandingPage,
} from "./screens-auth";
import { LandingPageV2, SplashScreenV2 } from "./landing-v2";
import {
  Dashboard,
  ProfilPage,
  MapScreen,
} from "./screens-main";

// === Lazy (chargés à la demande, splittés en chunks séparés du bundle initial) ===
// Chaque écran est dans son propre fichier sous src/screens/ et son
// propre chunk webpack — voir refactor de screens-detail.jsx (mai 2026).
// Confirmation de désinscription (lien « ne plus recevoir de message » du mail d'amorçage).
// Écran minuscule, autonome, sans thème : il s'affiche avant tout chargement de l'app.
// Confirmation avant de quitter, depuis l'accueil.
// ⚠️ Des VERBES, pas Oui/Non : devant « voulez-vous vraiment quitter ? », il faut
// relier « oui » a l'action avant de repondre, et c'est la qu'on se trompe.
const EXIT_T = {
  fr: { title: "Quitter HorseConnex ?", body: "Vous pourrez revenir quand vous voulez, rien ne sera perdu.", stay: "Rester", quit: "Quitter" },
  en: { title: "Leave HorseConnex?", body: "You can come back any time, nothing will be lost.", stay: "Stay", quit: "Leave" },
  de: { title: "HorseConnex verlassen?", body: "Sie können jederzeit zurückkommen, es geht nichts verloren.", stay: "Bleiben", quit: "Verlassen" },
  es: { title: "¿Salir de HorseConnex?", body: "Puedes volver cuando quieras, no se perderá nada.", stay: "Quedarme", quit: "Salir" },
  it: { title: "Uscire da HorseConnex?", body: "Puoi tornare quando vuoi, non andrà perso nulla.", stay: "Resta", quit: "Esci" },
  pt: { title: "Sair do HorseConnex?", body: "Podes voltar quando quiseres, nada será perdido.", stay: "Ficar", quit: "Sair" },
  nl: { title: "HorseConnex verlaten?", body: "Je kunt altijd terugkomen, er gaat niets verloren.", stay: "Blijven", quit: "Verlaten" },
};

const UNSUB_T = {
  fr: { ask: "Confirmez-vous le retrait ? Votre fiche sera supprimée de l'annuaire et vous ne recevrez plus aucun message de notre part.", yes: "Confirmer le retrait", no: "Annuler", busy: "Retrait en cours…", ok: "C'est fait : vous ne recevrez plus de message de HorseConnex et votre fiche a été retirée de l'annuaire.", ko: "Ce lien a déjà été utilisé ou n'est plus valable. Pour toute demande, répondez simplement au mail reçu : nous traitons chaque retrait sous 24 h.", home: "Découvrir l'annuaire" },
  en: { ask: "Confirm removal? Your listing will be deleted from the directory and you will not receive any further message from us.", yes: "Confirm removal", no: "Cancel", busy: "Removing…", ok: "Done: you will not receive any further message from HorseConnex and your listing has been removed from the directory.", ko: "This link has already been used or is no longer valid. Simply reply to the email you received: every removal is handled within 24 hours.", home: "Browse the directory" },
  de: { ask: "Löschung bestätigen? Ihr Eintrag wird aus dem Verzeichnis entfernt und Sie erhalten keine weitere Nachricht von uns.", yes: "Löschung bestätigen", no: "Abbrechen", busy: "Wird entfernt…", ok: "Erledigt: Sie erhalten keine weitere Nachricht von HorseConnex, und Ihr Eintrag wurde aus dem Verzeichnis entfernt.", ko: "Dieser Link wurde bereits verwendet oder ist nicht mehr gültig. Antworten Sie einfach auf die erhaltene E-Mail: jede Löschung wird innerhalb von 24 Stunden erledigt.", home: "Verzeichnis entdecken" },
  it: { ask: "Confermi la rimozione? La tua scheda sarà eliminata dall'elenco e non riceverai più alcun messaggio da parte nostra.", yes: "Conferma la rimozione", no: "Annulla", busy: "Rimozione in corso…", ok: "Fatto: non riceverai più messaggi da HorseConnex e la tua scheda è stata rimossa dall'elenco.", ko: "Questo link è già stato utilizzato o non è più valido. Rispondi semplicemente all'e-mail ricevuta: ogni rimozione viene gestita entro 24 ore.", home: "Scoprire l'elenco" },
  es: { ask: "¿Confirmas la retirada? Tu ficha se eliminará del directorio y no recibirás ningún otro mensaje nuestro.", yes: "Confirmar la retirada", no: "Cancelar", busy: "Retirando…", ok: "Hecho: no recibirás más mensajes de HorseConnex y tu ficha ha sido retirada del directorio.", ko: "Este enlace ya se ha utilizado o ya no es válido. Responde simplemente al correo recibido: cada retirada se gestiona en 24 h.", home: "Descubrir el directorio" },
  pt: { ask: "Confirma a remoção? A sua ficha será eliminada do diretório e não receberá mais nenhuma mensagem da nossa parte.", yes: "Confirmar a remoção", no: "Cancelar", busy: "A remover…", ok: "Feito: não receberá mais mensagens da HorseConnex e a sua ficha foi retirada do diretório.", ko: "Esta ligação já foi utilizada ou já não é válida. Responda simplesmente ao e-mail recebido: cada remoção é tratada em 24 h.", home: "Descobrir o diretório" },
  nl: { ask: "Bevestigt u de verwijdering? Uw vermelding wordt uit de gids verwijderd en u ontvangt geen berichten meer van ons.", yes: "Verwijdering bevestigen", no: "Annuleren", busy: "Bezig met verwijderen…", ok: "Klaar: u ontvangt geen berichten meer van HorseConnex en uw vermelding is uit de gids verwijderd.", ko: "Deze link is al gebruikt of is niet meer geldig. Antwoord gewoon op de ontvangen e-mail: elke verwijdering wordt binnen de 24 uur afgehandeld.", home: "De gids ontdekken" },
};
// Le retrait exige un CLIC sur cette page, il ne se déclenche jamais au chargement.
// Les scanners antispam de Google Workspace et Microsoft 365 ouvrent tous les liens
// d'un mail avant livraison : quand l'ouverture suffisait, ils supprimaient la fiche
// a la place du destinataire (2 cas sur 2, a 9 s et 55 s de l'envoi). C'est aussi ce
// qu'impose le RFC 8058, qui interdit un retrait sur simple GET.
function UnsubscribedScreen({ ok, proId, unsubKey, lang = "fr", onHome }) {
  const t = UNSUB_T[lang] || UNSUB_T.fr;
  const [etat, setEtat] = useState(ok === undefined ? "ask" : (ok ? "ok" : "ko"));
  // Garde SYNCHRONE : setEtat ne redessine pas avant la fin du handler, donc le
  // bouton "disabled" arrive trop tard pour un double-tap. Un 2e appel consommerait
  // une cle deja utilisee et afficherait "lien invalide" sur un retrait reussi.
  const enCours = useRef(false);
  const confirmer = async () => {
    if (enCours.current) return;
    enCours.current = true;
    setEtat("busy");
    let res = false;
    try { res = await unsubscribeProByKey(proId, unsubKey); } catch(e) { console.error("Unsubscribe error:", e); }
    setEtat(res ? "ok" : "ko");
  };
  return (
    <div style={{ minHeight: "100vh", background: "#ffffff", color: "#222222", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Arial, Helvetica, sans-serif" }}>
      <div style={{ maxWidth: 480, textAlign: "center" }}>
        <img src={LOGO} alt="HorseConnex" style={{ width: 120, marginBottom: 20 }} />
        <p style={{ fontSize: 16, lineHeight: 1.6, margin: "0 0 24px" }}>
          {etat === "ask" || etat === "busy" ? t.ask : (etat === "ok" ? t.ok : t.ko)}
        </p>
        {etat === "ask" || etat === "busy" ? (
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button type="button" onClick={confirmer} disabled={etat === "busy"} style={{ background: "#C8001A", color: "#ffffff", border: "none", borderRadius: 10, padding: "12px 22px", fontSize: 15, fontWeight: 700, cursor: etat === "busy" ? "default" : "pointer", opacity: etat === "busy" ? .6 : 1 }}>{etat === "busy" ? t.busy : t.yes}</button>
            <button type="button" onClick={onHome} disabled={etat === "busy"} style={{ background: "#ffffff", color: "#222222", border: "1px solid #cccccc", borderRadius: 10, padding: "12px 22px", fontSize: 15, fontWeight: 700, cursor: etat === "busy" ? "default" : "pointer" }}>{t.no}</button>
          </div>
        ) : (
          <button type="button" onClick={onHome} style={{ background: "#C8001A", color: "#ffffff", border: "none", borderRadius: 10, padding: "12px 22px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>{t.home}</button>
        )}
      </div>
    </div>
  );
}

const AdDetailScreen      = lazy(() => import("./screens/ad-detail").then(m => ({ default: m.AdDetailScreen })));
const ProDetailScreen     = lazy(() => import("./screens/pros").then(m => ({ default: m.ProDetailScreen })));
const ProFicheEditScreen  = lazy(() => import("./screens/pros").then(m => ({ default: m.ProFicheEditScreen })));
const PublishScreen       = lazy(() => import("./screens/publish").then(m => ({ default: m.PublishScreen })));
const MarketDetailScreen  = lazy(() => import("./screens/market-detail").then(m => ({ default: m.MarketDetailScreen })));
const ChatScreen          = lazy(() => import("./screens/chat").then(m => ({ default: m.ChatScreen })));
const FavoritesScreen     = lazy(() => import("./screens/favorites").then(m => ({ default: m.FavoritesScreen })));
const NotificationsScreen = lazy(() => import("./screens/notifications").then(m => ({ default: m.NotificationsScreen })));
const AgendaScreen        = lazy(() => import("./screens/agenda").then(m => ({ default: m.AgendaScreen })));
const HelpScreen          = lazy(() => import("./screens/help").then(m => ({ default: m.HelpScreen })));
const AboutScreen         = lazy(() => import("./screens/about").then(m => ({ default: m.AboutScreen })));
const SellerProfileScreen = lazy(() => import("./screens/seller-profile").then(m => ({ default: m.SellerProfileScreen })));
const LangSettingsScreen  = lazy(() => import("./screens/lang-settings").then(m => ({ default: m.LangSettingsScreen })));
// screens-horseroad : section dédiée (tous les users n'y vont pas)
const TabHorseRoad     = lazy(() => import("./screens-horseroad").then(m => ({ default: m.TabHorseRoad })));
const PublishHorseRoad = lazy(() => import("./screens-horseroad").then(m => ({ default: m.PublishHorseRoad })));
const HorseRoadDetail  = lazy(() => import("./screens-horseroad").then(m => ({ default: m.HorseRoadDetail })));
const HorseRoadMapScreen = lazy(() => import("./screens-horseroad").then(m => ({ default: m.HorseRoadMapScreen })));
// screens-profile : écrans profil avancés + admin
const MyAdsScreen           = lazy(() => import("./screens-profile").then(m => ({ default: m.MyAdsScreen })));
const MyReputationScreen    = lazy(() => import("./screens-profile").then(m => ({ default: m.MyReputationScreen })));
const RiderReputationScreen = lazy(() => import("./screens-profile").then(m => ({ default: m.RiderReputationScreen })));
const SecurityScreen        = lazy(() => import("./screens-profile").then(m => ({ default: m.SecurityScreen })));
const PremiumScreen         = lazy(() => import("./screens-profile").then(m => ({ default: m.PremiumScreen })));
const ProfileEditScreen     = lazy(() => import("./screens-profile").then(m => ({ default: m.ProfileEditScreen })));
const CarnetScreen          = lazy(() => import("./screens/carnet").then(m => ({ default: m.CarnetScreen })));
const SharedHorseScreen     = lazy(() => import("./screens/carnet").then(m => ({ default: m.SharedHorseScreen })));
const TabTrails             = lazy(() => import("./screens/trails-map").then(m => ({ default: m.TabTrails })));
const AddPoiScreen          = lazy(() => import("./screens/trails-map").then(m => ({ default: m.AddPoiScreen })));
const TrailDetailScreen     = lazy(() => import("./screens/trails").then(m => ({ default: m.TrailDetailScreen })));
const PublishTrailScreen    = lazy(() => import("./screens/trails").then(m => ({ default: m.PublishTrailScreen })));
const RecordTrailScreen     = lazy(() => import("./screens/trails").then(m => ({ default: m.RecordTrailScreen })));
const AdminPanel            = lazy(() => import("./screens-admin").then(m => ({ default: m.AdminPanel })));
// screens-auth : écrans secondaires (peu utilisés)
const ForgotPasswordScreen = lazy(() => import("./screens-auth").then(m => ({ default: m.ForgotPasswordScreen })));
const ContactSAVScreen     = lazy(() => import("./screens-auth").then(m => ({ default: m.ContactSAVScreen })));
const ComingSoonScreen     = lazy(() => import("./screens-auth").then(m => ({ default: m.ComingSoonScreen })));

// Loader affiché brièvement (~100-500ms) pendant qu'un chunk se télécharge.
// Garde le fond cohérent avec le thème pour éviter un flash blanc.
function LazyFallback() {
  return (
    <div style={{
      minHeight: "100vh",
      background: NIGHT_BG,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        border: "3px solid rgba(255,255,255,.12)",
        borderTopColor: "#C8001A",
        animation: "hcSpin 0.9s linear infinite",
      }} />
      <style>{"@keyframes hcSpin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
}

// Helper : ajoute le tag "Admin" si l'email match ADMIN_EMAIL.
// profile.categories ne contient PAS "Admin" en base — on le calcule à la volée
// pour que le bouton "Panneau Admin" sur le profil reste visible après un
// retour depuis AdminPanel (sans ça, le rechargement du profil par
// onAuthStateChange écrase le tag posé par LoginScreen).
function withAdminTag(cats, email) {
  const arr = cats || [];
  if (email && email.toLowerCase() === ADMIN_EMAIL.toLowerCase() && !arr.includes("Admin")) {
    return [...arr, "Admin"];
  }
  return arr;
}

// Helper : map un profil brut Supabase vers le shape user attendu par les composants.
// Extrait des 2 blocs setUser({...}) dupliques dans startup() et onAuthStateChange("SIGNED_IN").
function mapProfileToUser(profile, userId) {
  return {
    id: userId,
    prenom: profile.prenom || "",
    nom: profile.nom || "",
    name: (profile.prenom || "") + " " + (profile.nom || ""),
    email: profile.email || "",
    tel: profile.telephone || "",
    ville: profile.adresse || "",
    pays: profile.pays || "",
    bio: profile.bio || "",
    avatar: profile.avatar_url,
    badge: profile.badge,
    verified: profile.verified || false,
    cats: profile.categories || [],
    showTel: profile.show_telephone || false,
    showAdresse: profile.show_adresse || false,
    contactPhone: profile.contact_phone !== false,
    contactEmail: profile.contact_email || false,
    contactMessage: profile.contact_message !== false,
    nomEcurie: profile.nom_ecurie || "",
    nomPro: profile.nom_pro || "",
    isFounder: profile.is_founder || false,
    memberNo: profile.member_no || null,
    createdAt: profile.created_at || null,
    plan: profile.plan || "free",
    is_pro: profile.is_pro || false,
    pro_plan: profile.pro_plan || null,
    pro_since: profile.pro_since || null,
    pro_until: profile.pro_until || null,
    pro_reason: profile.pro_reason || null,
    display_name: profile.display_name || "",
    use_display_name: profile.use_display_name || false,
    // Sans cette ligne, user.country etait TOUJOURS undefined et le tri par
    // proximite retombait sur "CH" pour tout le monde, y compris les membres
    // belges ou espagnols.
    country: profile.country || "",
  };
}

// --- Proposition de compléter son profil : mémoire du refus -----------------
// Sans mémoire, « Plus tard » ne valait que pour l'affichage en cours et la
// fenêtre revenait à CHAQUE ouverture de l'app. On plafonne donc : 3 fois au
// total, jamais deux fois le même jour. L'entrée reste accessible dans le profil.
// Intention « réclamer ma fiche Pro » posée avant l'inscription (pros.jsx).
// ⚠️ Elle n'est PLUS effacée à la lecture : le popup de bienvenue Google puis
// l'onboarding renvoyaient sur l'accueil APRÈS qu'on l'ait consommée, et le pro
// ne retrouvait jamais sa fiche (Domaine d'Aunac, 9 sept. : il en a recréé une).
// Elle ne s'efface que sur réclamation réussie, suppression, ou expiration (1 h).
const CLAIM_TTL = 3600000;
function lireIntentionClaim() {
  try {
    const raw = localStorage.getItem("hc_claim_pro");
    if (!raw) return null;
    const [pid, pkey, ts] = raw.split("|");
    if (!pid || !ts || Date.now() - Number(ts) > CLAIM_TTL) { localStorage.removeItem("hc_claim_pro"); return null; }
    return { pid, pkey: pkey || null };
  } catch (e) { return null; }
}

const NUDGE_MAX = 3;
const NUDGE_DELAI = 24 * 3600 * 1000;

function lireNudge(userId) {
  try {
    const raw = localStorage.getItem("hc_profile_nudge_" + userId);
    if (!raw) return { n: 0, last: 0 };
    const v = JSON.parse(raw);
    return { n: Number(v.n) || 0, last: Number(v.last) || 0 };
  } catch (e) { return { n: 0, last: 0 }; }
}

function peutProposerProfil(userId) {
  if (!userId) return false;
  const { n, last } = lireNudge(userId);
  return n < NUDGE_MAX && Date.now() - last > NUDGE_DELAI;
}

function noterProfilRefuse(userId) {
  if (!userId) return;
  try {
    const { n } = lireNudge(userId);
    localStorage.setItem(
      "hc_profile_nudge_" + userId,
      JSON.stringify({ n: n + 1, last: Date.now() })
    );
  } catch (e) {}
}

export default function App() {
  // Deep link : si l'URL contient ?ad=ID (lien venant de Google via la page SEO),
  // on ouvrira directement la fiche annonce sans passer par splash/landing/auth.
  const initialDeepLinkAdId = (() => {
    try {
      if (typeof window === "undefined") return null;
      const params = new URLSearchParams(window.location.search);
      const adId = params.get("ad");
      if (adId && /^[a-f0-9-]{36}$/i.test(adId)) return adId;
      return null;
    } catch(e) { return null; }
  })();

  // Deep link trajet : ?trip=ID (lien de partage HorseRoad) ouvre directement
  // le trajet, sans passer par splash/landing/auth.
  const initialDeepLinkTripId = (() => {
    try {
      if (typeof window === "undefined") return null;
      const tripId = new URLSearchParams(window.location.search).get("trip");
      if (tripId && /^[a-f0-9-]{36}$/i.test(tripId)) return tripId;
      return null;
    } catch(e) { return null; }
  })();

  // Deep link fiche Pro : ?pro=ID (lien de partage / demarchage annuaire) ouvre
  // directement la fiche de l'annuaire, sans passer par splash/landing/auth.
  const initialDeepLinkProId = (() => {
    try {
      if (typeof window === "undefined") return null;
      const proId = new URLSearchParams(window.location.search).get("pro");
      if (proId && /^[a-f0-9-]{36}$/i.test(proId)) return proId;
      return null;
    } catch(e) { return null; }
  })();
  // Clé secrète du mail d'amorçage annuaire (?pro=ID&key=...) : autorise le
  // destinataire à réclamer OU supprimer sa fiche.
  const initialDeepLinkProKey = (() => {
    try {
      if (typeof window === "undefined") return null;
      const key = new URLSearchParams(window.location.search).get("key");
      if (key && /^[a-f0-9-]{36}$/i.test(key)) return key;
      return null;
    } catch(e) { return null; }
  })();
  // Lien « ne plus recevoir de message » du mail d'amorçage (?pro=ID&key=...&stop=1) :
  // désinscription en un clic, sans compte, exigée par le droit français (L34-5 CPCE).
  const initialDeepLinkStop = (() => {
    try {
      if (typeof window === "undefined") return false;
      return new URLSearchParams(window.location.search).get("stop") === "1";
    } catch(e) { return false; }
  })();
  const initialDeepLinkTrailId = (() => {
    try {
      if (typeof window === "undefined") return null;
      const id = new URLSearchParams(window.location.search).get("trail");
      if (id && /^[a-f0-9-]{36}$/i.test(id)) return id;
      return null;
    } catch(e) { return null; }
  })();
  // Lien direct vers la carte des randonnées : ?go=trails (partage, tests, réseaux sociaux). Invités + connectés.
  const initialDeepLinkGo = (() => {
    try {
      if (typeof window === "undefined") return null;
      const go = new URLSearchParams(window.location.search).get("go");
      return go === "trails" ? go : null;
    } catch(e) { return null; }
  })();

  // Lien de reinitialisation du mot de passe : l'email renvoie vers /reset-password
  // (le lien cree une session de recuperation). On detecte ce chemin pour afficher
  // l'ecran "nouveau mot de passe" en priorite, avant splash/landing/auth.
  const initialRecovery = (() => {
    try {
      if (typeof window === "undefined") return false;
      return window.location.pathname.replace(/\/+$/, "") === "/reset-password";
    } catch(e) { return false; }
  })();

  // Verifier l'acces beta au demarrage
  const getBetaAccess = () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const betaKey = urlParams.get("beta");
      if (betaKey === "horse26connexx") {
        localStorage.setItem("hc_beta_access", "true");
        // Nettoyer l'URL pour retirer ?beta=...
        window.history.replaceState({}, document.title, window.location.pathname);
        return true;
      }
      return localStorage.getItem("hc_beta_access") === "true";
    } catch(e) {
      return false;
    }
  };

  const [hasBetaAccess, setHasBetaAccess] = useState(getBetaAccess());
  // La landing fait partie de l'intro : on la rejoue à chaque ouverture de l'app
  // (sauf utilisateur déjà connecté, cf. showLanding). Pas de persistance navigateur,
  // sinon elle ne se réaffiche jamais une fois "entré" une première fois.
  const [hasSeenLanding, setHasSeenLanding] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(initialRecovery);
  // En mode aperçu (?v=1|2|3), on joue d'abord le splash v2 puis la landing v2.
  const [previewSplashDone, setPreviewSplashDone] = useState(false);
  // Quand l'utilisateur clique le CTA en mode aperçu, on bypass le "ré-affichage
  // forcé" de la landing pour qu'il puisse tester le flow d'après (auth, etc.).
  // Rafraîchir la page restitue la landing pour pouvoir comparer les versions.
  const [previewBypass, setPreviewBypass] = useState(false);

  // Popup installation PWA
  const [showInstallPopup, setShowInstallPopup] = useState(false);

  // Bandeau cookies
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  // Message « l'app a évolué » (membres existants ayant d'anciennes catégories).
  const [showEvolved, setShowEvolved] = useState(false);
  const [showFounder, setShowFounder] = useState(false);

  // Apercu des 2 variantes de la fenetre « membre fondateur », pour les tester sans
  // creer de compte. ⚠️ VOLONTAIREMENT INERTE EN PRODUCTION : la garde sur le nom
  // d'hote fait que ce bloc ne peut rien afficher sur www.horseconnex.com, meme si
  // ce fichier y est copie tel quel. Rien a retirer avant la synchro prod.
  const [founderPreview, setFounderPreview] = useState(() => {
    try {
      if (!/vercel\.app$/.test(window.location.hostname)) return null;
      const p = new URLSearchParams(window.location.search).get("preview");
      if (p === "founder") return { variant: "new", memberNo: 47 };
      if (p === "founder-old") return { variant: "existing", memberNo: 12 };
    } catch (e) {}
    return null;
  });
  // Message « crée ta fiche Pro » (pro sans fiche : nouvel inscrit pro ou passage en pro).
  const [showCreateFiche, setShowCreateFiche] = useState(false);

  // Verifier si on doit montrer la popup d'installation
  useEffect(() => {
    // Aout 2026 : popup d'installation PWA retire. L'app native est sur le
    // Play Store (et le site marche tres bien dans le navigateur) -> ce popup
    // ne faisait plus que gener les nouveaux visiteurs au moment de s'inscrire.
  }, []);

  // Consentement cookies : géré UNIQUEMENT sur l'écran légal (CGU), via la case
  // dédiée à côté de l'acceptation des CGU. Plus de bandeau séparé (on évite la
  // double demande / redondance). Le choix est mémorisé dans hc_cookies_consent.
  // (les blocs <CookieBanner/> restent dans le render mais ne sont plus déclenchés.)
  const [screen,setScreen] = useState("splash");
  const [params,setParams] = useState({});
  const [toast,setToast]   = useState(null);
  const [history,setHistory] = useState([]);
  const [userCats,setUserCats] = useState([]);
  const [lang,setLangState] = useState(() => {
    try {
      // 1. Choix sauvegardé par l'utilisateur (prioritaire)
      const saved = localStorage.getItem("hc_lang");
      if (saved && ["fr","en","de","it","es","pt","nl"].includes(saved)) return saved;
      // 2. Sinon, langue du système / navigateur
      const browserLang = (navigator.language || "en").substring(0, 2).toLowerCase();
      if (["fr","en","de","it","es","pt","nl"].includes(browserLang)) return browserLang;
    } catch(e) {}
    // 3. Fallback anglais (langue la plus parlée hors des 6 supportées)
    return "en";
  });

  // Depuis le 29 août 2026 les traductions sont chargées à la demande (src/i18n).
  // On charge la langue AVANT de la poser dans l'état : sinon le rendu se ferait
  // avec T[lang] encore absent, donc en français, puis rebasculerait — un clignotement.
  // Si le chargement échoue, on pose quand même la langue (le choix de l'utilisateur
  // est respecté et mémorisé) et l'app reste en français grâce au repli de useLang.
  const setLang = (l) => { loadLang(l).finally(() => setLangState(l)); };

  // Au démarrage, la langue initiale vient de localStorage ou du navigateur : elle
  // n'est pas passée par setLang, il faut donc la charger ici. Le compteur force
  // le re-rendu une fois la langue arrivée.
  const [, setI18nTick] = useState(0);
  useEffect(() => {
    let vivant = true;
    loadLang(lang).finally(() => { if (vivant) setI18nTick(n => n + 1); });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [user,setUser] = useState({prenom:"",nom:"",email:"",tel:"",ville:"",pays:"",bio:"",avatar:null,badge:null,verified:false,cats:[],showTel:false,showAdresse:false,contactPhone:true,contactEmail:false,contactMessage:true});
  const [authSession, setAuthSession] = useState(null);
  // profileNudge : propose (sans forcer) de compléter son profil après un
  // 1er login Google (profil sans catégories "qui suis-je"). Popup dismissible.
  // ⚠️ « Plus tard » ne mémorisait RIEN : la fenêtre revenait à chaque ouverture,
  // indéfiniment, tant que les catégories restaient vides. Signalé par une membre
  // connectée avec Google. On se souvient donc du refus : au plus 3 propositions
  // en tout, et jamais deux fois dans la même journée.
  const [profileNudge, setProfileNudge] = useState(false);
  // userLoaded : true une fois que la session a été vérifiée au démarrage.
  // Évite le "flash" Contacter→Modifier sur ma propre annonce le temps que la session se restaure.
  const [userLoaded, setUserLoaded] = useState(false);
  // Reglages globaux pilotes depuis l'Admin (mode maintenance, inscriptions).
  const [maintenance, setMaintenance] = useState(false);
  const [appSettings, setAppSettings] = useState(null);

  // Aperçu dev du thème Clair (New Modern App) : ?theme=light force le thème
  // clair sur toute l'app (hors écrans landing/auth verrouillés en nocturne),
  // sans persister le choix. Sert à tester le redesign avant l'activation finale.
  const previewTheme = () => { try { return new URLSearchParams(window.location.search).get("theme") === "light" ? "light" : null; } catch (e) { return null; } };

  // Appliquer le thème choisi (ou nocturne par défaut) au tout début, avant le 1er render visuel.
  // Tous les écrans utilisent NIGHT_BG (= var(--app-bg)) qui sera injectée par applyTheme.
  useEffect(() => { applyTheme(previewTheme() || getCurrentTheme()); }, []);

  // Push natif (app Capacitor uniquement) : dès qu'un utilisateur est connecté,
  // on enregistre le jeton FCM de l'appareil. No-op total sur le web normal.
  useEffect(() => { if (user?.id) initNativePush(); }, [user?.id]);
  // Bouton retour de l'app installee : sans ce relais, Android ferme l'app
  // avant que notre navigation ait son mot a dire. No-op dans un navigateur.
  useEffect(() => { initNativeBack(); }, []);

  // Mise a jour in-app (app Capacitor Android) : propose l'encart natif
  // "mise a jour disponible" quand une nouvelle version est sur le Play Store.
  // No-op total sur le web et tant que le plugin n'est pas dans le build.
  useEffect(() => { initAppUpdate(); }, []);

  // Force le thème "night" sur les écrans landing/auth quelle que
  // soit la préférence utilisateur — décision design : ces écrans
  // gardent l'identité visuelle premium dark (gradient nocturne +
  // sparkles + glassmorphism). Quand on quitte ces écrans, on
  // restaure le thème de l'utilisateur. Persist=false sur le
  // lock-night pour ne pas écraser le choix stocké en localStorage.
  useEffect(() => {
    const lockedNightScreens = ["splash","lang","legal","auth","login","register","onboarding","forgotPassword","contactSAV","comingSoon","landing"];
    if (lockedNightScreens.includes(screen)) {
      applyTheme("night", false);
    } else {
      applyTheme(previewTheme() || getCurrentTheme());
    }
  }, [screen]);

  // Trafic app : compter l'ouverture (mode découverte inclus, 1×/session navigateur)
  useEffect(() => { trackAppOpen(lang); trackVisitorSource(lang); trackVisitorCountry(lang); }, []);

  // Message « l'app a évolué » : montré 1 fois aux membres existants qui ont encore
  // d'anciennes catégories, une fois arrivés sur le dashboard. Non bloquant.
  useEffect(() => {
    try {
      if (screen === "dash" && user && user.id && needsProfileUpdate(user.cats)) {
        if (!localStorage.getItem("hc_evolved_seen_" + user.id)) setShowEvolved(true);
      }
    } catch(e) {}
  }, [screen, user]);

  // Message « membre fondateur » : montré 1 fois aux 150 premiers inscrits, sur le
  // dashboard. Deux variantes : ceux inscrits AVANT le lancement de l'offre sont
  // remerciés, les suivants sont accueillis. On passe apres EvolvedNotice pour ne
  // jamais empiler deux fenetres.
  useEffect(() => {
    try {
      if (screen !== "dash" || !user || !user.id || !user.isFounder) return;
      if (needsProfileUpdate(user.cats)) return; // laisser passer EvolvedNotice d'abord
      if (localStorage.getItem("hc_founder_seen_" + user.id)) return;
      setShowFounder(true);
    } catch(e) {}
  }, [screen, user]);

  // Message « crée ta fiche Pro » : montré 1 fois à un pro (taxonomie à jour) qui n'a
  // pas encore de fiche, sur le dashboard. Couvre inscription pro ET passage en pro.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (screen !== "dash" || !user || !user.id) return;
        const isPro = (user.cats || []).some(c => PRO_METIERS[c]);
        if (!isPro || needsProfileUpdate(user.cats)) return; // laisser passer EvolvedNotice d'abord
        if (localStorage.getItem("hc_fiche_nudge_" + user.id)) return;
        const mine = await getMyPros();
        if (!cancelled && (!mine || mine.length === 0)) setShowCreateFiche(true);
      } catch(e) {}
    })();
    return () => { cancelled = true; };
  }, [screen, user]);

  // Cookies (juillet 2026) : plus de popup au démarrage pour tout le monde (ça
  // faisait doublon avec le consentement pris à l'inscription). Mais les VISITEURS
  // SANS COMPTE (mode découverte) doivent tout de même avoir une notice (RGPD) :
  // on la montre uniquement quand un invité NAVIGUE (écran dash) et n'a pas encore
  // consenti. Un inscrit consent déjà à l'inscription -> ne la voit jamais.
  // Aout 2026 : bandeau invite retire aussi (il s'empilait avec d'autres popups
  // pile au moment ou un nouveau visiteur decouvre l'app). Le consentement est
  // recueilli a l'inscription (ecran CGU + case dediee) ; les invites ne font
  // que consulter du contenu public sans traceur tiers.
  useEffect(() => {}, [screen, user]);

  // Vérifier la session au démarrage
  useEffect(() => {
    const startup = async () => {
      // Mode maintenance : on interroge les reglages AVANT toute autre chose, pour
      // qu'un visiteur non connecte le voie aussi. L'admin, lui, garde son acces —
      // sinon activer le mode maintenance reviendrait a se verrouiller dehors.
      try {
        const s = await getAppSettings();
        setAppSettings(s);
        if (s.maintenance) {
          // ⚠️ Le cas qui verrouille dehors : admin DÉCONNECTÉ pendant la maintenance.
          // L'écran de maintenance masque celui de connexion, donc il ne pourrait
          // plus se reconnecter pour la désactiver. La clé ?preview= est la porte
          // de secours : on la teste ici AUSSI, sinon on couperait le démarrage
          // avant même d'avoir chargé l'app pour le porteur de la clé.
          const bypass = (new URLSearchParams(window.location.search).get("preview")) === "HorseConnex2025";
          const { data: { session } } = await supabase.auth.getSession();
          const email = (session?.user?.email || "").toLowerCase();
          if (!bypass && email !== ADMIN_EMAIL.toLowerCase()) {
            setMaintenance(true);
            setUserLoaded(true);
            return;
          }
        }
      } catch(e) { /* une panne de lecture ne doit jamais fermer l'app */ }

      // Reset password via lien email : on etablit la session de recuperation de
      // facon DETERMINISTE (awaitee) AVANT d'afficher l'ecran, pour tous les formats
      // de lien possibles. Sinon on depend de detectSessionInUrl (async) et, si le
      // lien est un ?code= PKCE ouvert dans un AUTRE navigateur que le demandeur, il
      // n'y a pas de code_verifier local -> aucune session -> updateUser echoue
      // ("Auth session missing") -> faux "Lien expire".
      //   - #access_token=...&type=recovery : flux implicit (envoi via supabaseResetSender),
      //     cas nominal cross-device -> setSession direct avec les jetons du fragment.
      //   - ?token_hash=...&type=recovery    : template email token_hash (si active un jour).
      //   - ?code=...                         : flux PKCE historique -> ne marche QUE same-device.
      try {
        const isRecoveryPath = window.location.pathname.replace(/\/+$/, "") === "/reset-password";
        const qp = new URLSearchParams(window.location.search);
        const hp = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
        const hashAccess = hp.get("access_token");
        const hashRefresh = hp.get("refresh_token");
        const hashType = hp.get("type");
        const tokenHash = qp.get("token_hash");
        const qType = qp.get("type");
        const code = qp.get("code");
        const hasRecoveryTokens = !!((hashAccess && isRecoveryPath) || (tokenHash && qType === "recovery") || (code && isRecoveryPath));

        if (isRecoveryPath || hasRecoveryTokens) {
          try {
            if (hashAccess) {
              // Flux implicit : jetons directement dans le #fragment.
              const { error } = await supabase.auth.setSession({ access_token: hashAccess, refresh_token: hashRefresh });
              if (error) console.error("reset setSession:", error);
            } else if (tokenHash && qType === "recovery") {
              const { error } = await supabase.auth.verifyOtp({ type: "recovery", token_hash: tokenHash });
              if (error) console.error("reset verifyOtp:", error);
            } else if (code) {
              // PKCE : ne reussit que dans le meme navigateur (verifier local).
              const { error } = await supabase.auth.exchangeCodeForSession(code);
              if (error) console.error("reset exchangeCodeForSession:", error);
            }
          } catch(e) { console.error("reset establish session:", e); }
          // detectSessionInUrl a pu deja poser la session ; on confirme.
          let recSession = null;
          try { recSession = (await supabase.auth.getSession()).data.session; } catch(e) {}
          if (!recSession) console.warn("reset: aucune session de recuperation etablie (lien PKCE cross-device, expire, ou deja utilise)");
          // Nettoyer l'URL (jetons usage unique) mais garder /reset-password pour l'ecran dedie.
          try { window.history.replaceState({}, document.title, "/reset-password"); } catch(e) {}
          setRecoveryMode(true);
          setUserLoaded(true);
          return; // ecran "nouveau mot de passe" prioritaire, on stoppe le startup normal
        }
      } catch(e) { console.error("Recovery link error:", e); }

      const { data: { session } } = await supabase.auth.getSession();
      let hasProfile = false;
      let incomplete = false;
      if (session?.user) {
        setAuthSession(session);
        const { data: profile } = await supabase.from("profiles").select("*").eq("id", session.user.id).single();
        if (profile) {
          // Compte suspendu : sign-out immédiat + alerte. L'utilisateur n'entre
          // pas dans l'app (mais la suspension reste réversible côté admin).
          if (profile.suspended_at) {
            await supabase.auth.signOut();
            setAuthSession(null);
            setTimeout(() => {
              alert(`Votre compte HorseConnex a été suspendu.\nMotif : ${profile.suspension_reason || "Violation des CGU"}\n\nContactez l'administration si vous pensez qu'il s'agit d'une erreur.`);
            }, 100);
          } else {
            hasProfile = true;
            // Profil minimal après 1er login Google : pas encore de catégories
            // "qui suis-je" → on enverra l'utilisateur compléter son profil.
            incomplete = !profile.categories || profile.categories.length === 0;
            setUser(mapProfileToUser(profile, session.user.id));
            setUserCats(withAdminTag(profile.categories, session.user.email));
            try {
              const savedLang = localStorage.getItem("hc_lang");
              if (savedLang) setLang(savedLang);
            } catch(e) {}
          }
        }
      }

      // Deep link prioritaire : si on a un ?ad=ID, on ouvre direct la fiche
      // (skip splash/lang/legal/auth/LandingPage). Marche pour invités ET connectés.
      if (initialDeepLinkAdId) {
        try {
          let { data: a } = await supabase
            .from("ads")
            .select("id, user_id, category, title, description, translations, price, location, latitude, longitude, lat, lng, photos, tags, tarifs, infras, boxes, prestations, age_range, status, views, boosted, created_at, location_type, coverage_regions, country, region, city, hide_from_map, horse_breed, horse_age, horse_sex, horse_discipline, horse_level, horse_height, contact_phone, contact_email, contact_message, video_url, videos")
            .eq("id", initialDeepLinkAdId)
            .eq("status", "active")
            .maybeSingle();
          // Blocage bidirectionnel : ne pas ouvrir l'annonce d'un utilisateur bloqué
          const blockedIds = await getBlockedIds();
          if (a && blockedIds.has(a.user_id)) a = null;
          if (a) {
            // Infos vendeur via la vue publique (profiles direct = RLS own+admin only)
            let sellerProf = null;
            if (a.user_id) {
              const { data: sp } = await supabase
                .from("profiles_public")
                .select("id, prenom, nom, avatar_url, verified, badge, display_name, use_display_name")
                .eq("id", a.user_id)
                .maybeSingle();
              sellerProf = sp;
            }
            let displayLoc = a.location || "";
            if (a.location_type === "region" && a.region) displayLoc = a.region;
            else if (a.location_type === "coverage" && a.coverage_regions?.length > 0) displayLoc = "🚐 " + a.coverage_regions.slice(0,3).join(", ") + (a.coverage_regions.length>3 ? " +" + (a.coverage_regions.length-3) : "");
            else if (a.location_type === "precise" && a.city) displayLoc = a.city + (a.region ? ", " + a.region : "");
            const formatted = {
              id: a.id, cat: a.category, title: a.title, desc: a.description,
              translations: a.translations || null,
              price: a.price, loc: displayLoc, photos: a.photos || [], tags: a.tags || [],
              tarifs: a.tarifs || [], infras: a.infras || [], boxes: a.boxes || [],
              prestations: a.prestations || [], ageRange: a.age_range, locationType: a.location_type,
              country: a.country, region: a.region, city: a.city, lat: a.lat, lng: a.lng,
              hideFromMap: a.hide_from_map || false,
              contact_phone: a.contact_phone, contact_email: a.contact_email, contact_message: a.contact_message,
              coverageRegions: a.coverage_regions || [],
              date: new Date(a.created_at).toLocaleDateString("fr-FR"),
              video: a.video_url || null,
              videos: a.videos || [],
              seller: getDisplayName(sellerProf),
              verified: sellerProf?.verified || false, badge: sellerProf?.badge,
              avatar: sellerProf?.avatar_url, views: a.views || 0,
              user_id: a.user_id, sellerId: a.user_id,
              horseBreed: a.horse_breed, horseAge: a.horse_age, horseSex: a.horse_sex,
              horseDiscipline: a.horse_discipline || [], horseLevel: a.horse_level, horseHeight: a.horse_height,
            };
            // Skip LandingPage et auth, on ouvre direct la fiche.
            try { localStorage.setItem("hc_landing_seen", "true"); } catch(e) {}
            setHasSeenLanding(true);
            // Historique : back depuis la fiche → dashboard
            setHistory([{screen: "dash", params: {}}]);
            setTimeout(() => {
              setScreen("adDetail");
              setParams({ ad: formatted });
              setUserLoaded(true);
            }, 300);
            return;
          }
        } catch(e) { console.error("Deep link ad error:", e); }
      }

      // Deep link trajet : ?trip=ID ouvre directement la fiche trajet HorseRoad.
      // Marche pour invités ET connectés. On passe la ligne brute carpool_trips
      // (HorseRoadDetail consomme directement les colonnes de la table).
      if (initialDeepLinkTripId) {
        try {
          let { data: tr } = await supabase
            .from("carpool_trips")
            .select("*")
            .eq("id", initialDeepLinkTripId)
            .eq("status", "active")
            .is("deleted_at", null)
            .maybeSingle();
          // Blocage bidirectionnel : ne pas ouvrir le trajet d'un utilisateur bloqué
          const blockedIds = await getBlockedIds();
          if (tr && blockedIds.has(tr.user_id)) tr = null;
          if (tr) {
            try { localStorage.setItem("hc_landing_seen", "true"); } catch(e) {}
            setHasSeenLanding(true);
            setHistory([{screen: "dash", params: {}}]);
            setTimeout(() => {
              setScreen("horseroadDetail");
              setParams({ trip: tr });
              setUserLoaded(true);
            }, 300);
            return;
          }
        } catch(e) { console.error("Deep link trip error:", e); }
      }

      // Deep link itinéraire : ?trail=ID ouvre la fiche randonnée. Invités + connectés.
      if (initialDeepLinkTrailId) {
        try {
          const { data: tl } = await supabase
            .from("trails").select("*").eq("id", initialDeepLinkTrailId)
            .eq("status", "active").is("deleted_at", null).maybeSingle();
          if (tl) {
            try { localStorage.setItem("hc_landing_seen", "true"); } catch(e) {}
            setHasSeenLanding(true);
            setHistory([{screen: "dash", params: {}}]);
            setTimeout(() => { setScreen("trailDetail"); setParams({ trail: tl }); setUserLoaded(true); }, 300);
            return;
          }
        } catch(e) { console.error("Deep link trail error:", e); }
      }
      if (initialDeepLinkGo === "trails") {
        try { localStorage.setItem("hc_landing_seen", "true"); } catch(e) {}
        try { window.history.replaceState({}, document.title, window.location.pathname); } catch(e) {}
        setHasSeenLanding(true);
        setHistory([{screen: "dash", params: {}}]);
        setTimeout(() => { setScreen("trails"); setParams({}); setUserLoaded(true); }, 300);
        return;
      }

      // Désinscription depuis le mail d'amorçage : on N'EXÉCUTE RIEN ICI. On ouvre une page
      // qui demande un clic de confirmation — un scanner antispam charge la page mais ne
      // clique pas. Le retrait lui-même est fait par UnsubscribedScreen.
      if (initialDeepLinkProId && initialDeepLinkProKey && initialDeepLinkStop) {
        try { localStorage.setItem("hc_landing_seen", "true"); } catch(e) {}
        try { window.history.replaceState({}, document.title, window.location.pathname); } catch(e) {}
        setHasSeenLanding(true);
        setHistory([{screen: "dash", params: {tab:"pros"}}]);
        setTimeout(() => { setScreen("unsubscribed"); setParams({ proId: initialDeepLinkProId, unsubKey: initialDeepLinkProKey }); setUserLoaded(true); }, 300);
        return;
      }

      // Deep link fiche Pro : ?pro=ID ouvre la fiche annuaire. Invités + connectés.
      if (initialDeepLinkProId) {
        try {
          const pro = await getProById(initialDeepLinkProId);
          if (pro) {
            try { localStorage.setItem("hc_landing_seen", "true"); } catch(e) {}
            setHasSeenLanding(true);
            setHistory([{screen: "dash", params: {tab:"pros"}}]);
            setTimeout(() => { setScreen("proDetail"); setParams({ pro, claimKey: initialDeepLinkProKey }); setUserLoaded(true); }, 300);
            return;
          }
        } catch(e) { console.error("Deep link pro error:", e); }
      }

      // Flux normal : si connecté, on file au dashboard. Sauf profil incomplet
      // (1er login Google sans catégories) → écran de complétion de profil.
      if (hasProfile) {
        // Profil incomplet (1er login Google) : on va au dashboard et on PROPOSE
        // de compléter le profil (popup dismissible) au lieu d'imposer l'onboarding.
        setTimeout(() => { setScreen("dash"); if (incomplete && peutProposerProfil(session.user.id)) setProfileNudge(true); setUserLoaded(true); }, 300);
      } else {
        setUserLoaded(true);
      }
    };
    startup();

    // Écouter les changements de session.
    // IMPORTANT : la callback reste synchrone et ne fait AUCUN appel supabase await
    // directement dedans. Supabase-js tient un verrou interne pendant l'événement ;
    // un await supabase.* ici provoque un deadlock (signInWithPassword ne répond
    // jamais, bouton figé). On diffère donc le fetch profil via setTimeout(0).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setAuthSession(session);
      // Lien email de reinitialisation -> afficher l'ecran "nouveau mot de passe".
      if (event === "PASSWORD_RECOVERY") setRecoveryMode(true);
      // Sur un login frais (event SIGNED_IN), recharger le profil pour alimenter
      // le state user : LoginScreen ne reçoit pas setUser et ne le fait pas,
      // donc sans ça user.id reste vide et les checks !user?.id (Dashboard,
      // AdDetail, HorseRoad) déclenchent les popups SignupPrompt à tort.
      // INITIAL_SESSION (mount initial) est déjà géré par startup() ci-dessus.
      if (event === "SIGNED_IN" && session?.user) {
        setTimeout(async () => {
          try {
            if (reclamerRef.current) { try { await reclamerRef.current(); } catch (e) {} }
            const { data: profile } = await supabase.from("profiles").select("*").eq("id", session.user.id).single();
            if (profile) {
              if (profile.suspended_at) {
                await supabase.auth.signOut();
                setAuthSession(null);
                setUser(null);
                setUserCats([]);
                alert(`Votre compte HorseConnex a été suspendu.\nMotif : ${profile.suspension_reason || "Violation des CGU"}\n\nContactez l'administration si vous pensez qu'il s'agit d'une erreur.`);
                return;
              }
              setUser(mapProfileToUser(profile, session.user.id));
              setUserCats(withAdminTag(profile.categories, session.user.email));
              // 1er login Google "a chaud" (depuis le bouton du mur d'inscription) :
              // profil sans categories "qui suis-je" -> on PROPOSE de completer le
              // profil (popup dismissible), sans forcer l'onboarding.
              if ((!profile.categories || profile.categories.length === 0) && peutProposerProfil(session.user.id)) {
                setProfileNudge(true);
              }
              // Intention « réclamer ma fiche Pro » posée avant l'inscription :
              // on ramène l'utilisateur sur sa fiche. Passe par un ref pour tenir
              // la version courante de navigate (ce handler n'est créé qu'une fois).
              try { setTimeout(() => { if (allerRef.current) allerRef.current(); }, 400); } catch(e) {}
            }
          } catch(e) { console.error("Erreur fetch profil apres login:", e); }
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(()=>setToast(null), msg.duration || 2200);
  };

  // Le bouton retour d'Android consomme une entrée de l'historique du NAVIGATEUR.
  // Notre pile d'écrans et celle du navigateur doivent donc rester alignées : dès
  // qu'elles divergent, le retour ne fait plus rien puis FERME l'app. On compte
  // les entrées qu'on a nous-mêmes empilées pour ne jamais en consommer une de trop.
  const pushedRef = useRef(0);
  const applyBackRef = useRef(null);
  // L'écouteur popstate n'est posé qu'une fois : il lit l'état courant par refs.
  const historyLenRef = useRef(0);
  const [exitConfirm, setExitConfirm] = useState(false);

  // Sortie réelle de l'application, une fois la confirmation donnée.
  // ⚠️ Le verrou est indispensable : la sortie repasse elle-même par l'historique,
  // donc sans lui notre propre `popstate` ré-ouvrirait la fenêtre à l'infini.
  const exitingRef = useRef(false);
  const doExit = () => {
    setExitConfirm(false);
    exitingRef.current = true;
    const nativeApp = (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App) || null;
    if (nativeApp && nativeApp.exitApp) { try { nativeApp.exitApp(); return; } catch(e) {} }
    // Hors app native, « quitter » = repasser derrière TOUTES nos entrées d'un
    // coup. Différé : un saut d'historique demandé pendant le traitement de
    // `popstate` est ignoré par le navigateur.
    const saut = (pushedRef.current || 0) + 1;
    setTimeout(() => { try { window.history.go(-saut); } catch(e) {} }, 0);
  };

  // Ouvrir l'app directement sur une fiche (?ad=, ?trip=, ?trail=, ?pro=) posait
  // un écran d'historique applicatif SANS entrée navigateur correspondante : le
  // premier appui sur retour quittait l'application au lieu de revenir à l'accueil.
  const seedBackEntry = () => {
    try { window.history.pushState({ hc: "seed" }, "", ""); pushedRef.current += 1; } catch(e) {}
  };

  const navigate = (sc, p={}) => {
    setHistory(h=>[...h,{screen,params}]);
    setScreen(sc);
    setParams(p);
    try { window.history.pushState({ hc: sc }, "", ""); pushedRef.current += 1; } catch(e) {}
  };

  // Retour sur la fiche à réclamer. La connexion (SIGNED_IN) et la fin
  // d'inscription peuvent toutes deux y mener à quelques centaines de ms
  // d'écart : on ne pousse la fiche qu'une fois (fenêtre de 5 s). Passé ce
  // délai — typiquement après l'onboarding Google — on y retourne bel et bien.
  const claimNavRef = useRef({ pid: null, at: 0 });
  const allerSurFicheAReclamer = async () => {
    const it = lireIntentionClaim();
    if (!it) return false;
    if (claimNavRef.current.pid === it.pid && Date.now() - claimNavRef.current.at < 5000) return true;
    try {
      const pro = await getProById(it.pid);
      if (!pro) return false;
      claimNavRef.current = { pid: it.pid, at: Date.now() };
      navigate("proDetail", { pro, claimKey: it.pkey });
      return true;
    } catch (e) { return false; }
  };
  const allerRef = useRef(null);
  allerRef.current = allerSurFicheAReclamer;

  // Fiche d'amorçage dont l'email est celui du compte (vérifié) : rattachée automatiquement
  // à la connexion et à la fin d'inscription. Avant le 17 oct. 2026 ce n'était proposé que
  // dans l'écran « Créer ma fiche », et aucun des pros inscrits n'avait retrouvé sa fiche.
  const reclamerParEmail = async () => {
    try {
      const found = await findUnclaimedProForMe();
      if (!found) return false;
      const ok = await claimProByEmail(found.id);
      if (ok) { showToast({ type: "success", text: `${found.name} : fiche rattachée à votre compte` }); return true; }
    } catch (e) {}
    return false;
  };
  const reclamerRef = useRef(null);
  reclamerRef.current = reclamerParEmail;

  // Fin d'inscription (email comme Google) : la fiche à réclamer passe avant
  // l'accueil, sinon le pro atterrit sur le dashboard et ne la retrouve plus.
  const finirInscription = async (cats) => {
    setUserCats(cats);
    if (await allerSurFicheAReclamer()) return;
    await reclamerParEmail();
    navigate("dash");
  };

  // Retour appliqué à l'état de l'app seulement. Déclenché par popstate, donc
  // après que le navigateur a déjà dépilé son entrée.
  const applyBack = () => {
    if(history.length){
      const prev=history[history.length-1];
      setHistory(h=>h.slice(0,-1));
      setScreen(prev.screen);
      setParams(prev.params||{});
    }
  };
  applyBackRef.current = applyBack;
  historyLenRef.current = history.length;

  // Flèche retour DANS l'app : on passe par l'historique du navigateur au lieu de
  // dépiler nous-mêmes, sinon chaque retour laisse une entrée orpheline — et le
  // bouton Android finit par ne rien faire, puis par quitter l'app.
  const goBack = () => {
    if (pushedRef.current > 0) {
      try { window.history.back(); return; } catch(e) {}
    }
    applyBack();
  };

  // Bouton retour physique Android / swipe iOS. Écouteur posé UNE seule fois :
  // il lit l'état courant via la ref, sans se réabonner à chaque navigation.
  useEffect(() => {
    const handlePopState = () => {
      if (pushedRef.current > 0) pushedRef.current -= 1;
      // L'ecran affiche a-t-il sa propre navigation interne a reculer d'un cran
      // (onglet, etape de publication, fiche du carnet) ? Si oui il consomme le
      // retour, et on re-arme une entree pour que l'appui suivant marche aussi.
      if (runBackHandlers()) { seedBackEntry(); return; }
      // Depuis l'accueil il n'y a plus rien à dépiler, et un seul appui fermait
      // l'app — souvent par accident. On demande confirmation.
      // ⚠️ On ne PIÈGE pas le bouton retour : le second appui sort réellement.
      // Une app dont on ne peut plus sortir est un défaut, pas une protection.
      if (!historyLenRef.current) {
        if (exitingRef.current) return;   // sortie confirmée : on laisse partir
        // Le retour pendant que la fenêtre est ouverte la referme : le choix sûr
        // reste le choix par défaut, on ne quitte jamais sans l'avoir demandé.
        setExitConfirm(v => !v);
        seedBackEntry();
        return;
      }
      applyBackRef.current && applyBackRef.current();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // ⚠️ Le bouton retour d'Android ne prévient l'application QUE s'il a une entrée
  // d'historique à dépiler. Sans elle, il quitte l'app sans déclencher `popstate`
  // — donc sans consulter le moindre intercepteur. Or on sort de la landing par
  // un simple changement d'écran, sans empiler quoi que ce soit : le tableau de
  // bord se retrouvait sans aucune entrée, et le premier retour fermait l'app.
  // On pose donc une entrée « sentinelle » dès que la landing est passée. La
  // landing, elle, reste bien le point de sortie normal de l'application.
  // ⚠️ La condition doit être celle de `showLanding` plus bas, sinon on ne couvre
  // qu'une partie des membres : un utilisateur DÉJÀ CONNECTÉ ne voit jamais la
  // landing, donc `hasSeenLanding` reste faux pour lui — et la sentinelle n'était
  // jamais posée. Le correctif ne marchait que pour les visiteurs sans compte.
  const guardRef = useRef(false);
  useEffect(() => {
    if (!(hasSeenLanding || authSession) || guardRef.current) return;
    guardRef.current = true;
    seedBackEntry();
  }, [hasSeenLanding, authSession]);

  // Clé secrète pour bypasser la maintenance
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const previewKey = urlParams ? urlParams.get("preview") : null;
  const isOwner = previewKey === "HorseConnex2025";

  // Afficher la page maintenance si activée (sauf pour le propriétaire)
  // Le mode maintenance vient soit de la constante (fige au build), soit du
  // reglage pilote depuis l'Admin. L'admin connecte n'est jamais bloque.
  if((MAINTENANCE_MODE || maintenance) && !isOwner) return (
    <div style={{maxWidth:430,margin:"0 auto",minHeight:"100vh",background:"var(--dark2)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,fontFamily:"var(--fb)"}}>
      <G/>
      <div style={{textAlign:"center",animation:"fadeUp .5s ease"}}>
        <img src={LOGO} alt="HorseConnex" style={{width:160,height:160,objectFit:"contain",marginBottom:24}}/>
        <div style={{fontFamily:"var(--fh)",fontSize:30,fontWeight:900,color:"#fff",letterSpacing:1,marginBottom:8}}>
          HORSE<span style={{color:"var(--red)"}}>CONNEX</span>
        </div>
        <div style={{fontSize:12,color:"rgba(255,255,255,.35)",letterSpacing:3,textTransform:"uppercase",marginBottom:40}}>Connecte le monde équestre</div>
        <div style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:16,padding:"24px 20px",marginBottom:28}}>
          <div style={{fontSize:36,marginBottom:12}}>🔧</div>
          <div style={{fontFamily:"var(--fh)",fontSize:20,fontWeight:800,color:"#fff",marginBottom:8}}>Site en maintenance</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,.5)",lineHeight:1.7}}>
            Nous travaillons à améliorer votre expérience.<br/>
            HorseConnex sera disponible très prochainement.
          </div>
        </div>
        <div style={{fontSize:12,color:"rgba(255,255,255,.25)",marginBottom:16}}>Une question ?</div>
        <a href="mailto:info@horseconnex.com" style={{color:"var(--red)",fontSize:13,fontWeight:600,textDecoration:"none"}}>info@horseconnex.com</a>
      </div>
    </div>
  );

  // Deep link PUBLIC : fiche santé partagée (?horse=token) — aucune connexion
  // requise, lit via la RPC get_shared_horse (token = capacité). Avant tout le reste.
  const sharedHorseToken = urlParams ? urlParams.get("horse") : null;
  if (sharedHorseToken) {
    return (
      <div style={{maxWidth:430,margin:"0 auto",minHeight:"100vh",position:"relative",fontFamily:"var(--fb)"}}>
        <G/>
        <Suspense fallback={<div style={{padding:60,textAlign:"center",color:"var(--hc-text-muted)"}}>…</div>}>
          <SharedHorseScreen token={sharedHorseToken} lang={lang}/>
        </Suspense>
      </div>
    );
  }

  // Réinitialisation du mot de passe (lien email /reset-password) : écran dédié,
  // priorité absolue, avant splash/landing/auth.
  if (recoveryMode) {
    return (
      <div style={{maxWidth:430,margin:"0 auto",minHeight:"100vh",position:"relative",fontFamily:"var(--fb)"}}>
        <G/>
        {toast && <Toast msg={toast}/>}
        <ResetPasswordScreen lang={lang} showToast={showToast} onDone={()=>{
          try { window.history.replaceState({}, document.title, "/"); } catch(e){}
          // Reload propre : la session de recuperation est persistee -> startup()
          // recharge le profil et route correctement (dash / onboarding).
          setTimeout(() => { try { window.location.replace("/"); } catch(e){ setRecoveryMode(false); } }, 900);
        }}/>
      </div>
    );
  }

  // Si l'utilisateur n'a jamais vu la LandingPage, l'afficher en premier
  // Mode aperçu : ?v=0|1|2|3 force l'affichage de la landing même si déjà vue
  // (utile pour comparer les versions design sur la branche de prévisualisation).
  let forcePreviewVersion = null;
  try {
    const v = new URLSearchParams(window.location.search).get("v");
    if (v === "0" || v === "1" || v === "2" || v === "3") forcePreviewVersion = v;
  } catch(e) {}
  const isPreviewMode = forcePreviewVersion !== null;
  const useOldDesign = forcePreviewVersion === "0";

  // En mode aperçu : on joue d'abord le splash (v2 si nouveau design, v1 si ancien),
  // puis on enchaîne sur la landing demandée.
  // Splash d'intro : joué au tout premier affichage, aussi bien en mode aperçu
  // qu'en visite normale fraîche (avant la landing), avec le splash étoiles v2.
  const needsIntroSplash = !previewSplashDone && (isPreviewMode || !hasSeenLanding);
  if (needsIntroSplash) {
    return (
      <>
        <G/>
        {useOldDesign
          ? <SplashScreen onDone={()=>setPreviewSplashDone(true)} lang={lang}/>
          : <SplashScreenV2 onDone={()=>setPreviewSplashDone(true)}/>}
      </>
    );
  }

  // En mode preview, on garde la landing affichée tant que le CTA n'a pas
  // été cliqué (previewBypass=false). Une fois cliqué, on suit le flow normal
  // pour pouvoir tester l'auth, le dashboard, etc.
  // ⚠️ `userLoaded` est indispensable ici. Tant que la session n'a pas été
  // vérifiée, `authSession` est vide — on ne sait donc PAS encore si le visiteur
  // est connecté. Afficher la landing à ce moment-là montre une page d'accueil
  // publique, puis un écran de connexion, à un membre qui EST déjà connecté :
  // il croit devoir se reconnecter à chaque ouverture. C'est d'autant plus
  // visible dans l'app installée, dont le démarrage à froid est plus lent.
  // On attend le verdict ; pendant ce temps le splash reste affiché.
  const showLanding = isPreviewMode ? !previewBypass : (userLoaded && !hasSeenLanding && !authSession);
  if (showLanding) {
    const handleEnter = () => {
      try { localStorage.setItem("hc_landing_seen", "true"); } catch(e){}
      setHasSeenLanding(true);
      if (isPreviewMode) {
        // En mode aperçu : on saute splash/lang/legal pour aller direct
        // à l'écran de choix (auth), bien plus utile pour tester le flow.
        setPreviewBypass(true);
        setScreen("auth");
      } else {
        // Flow normal : la langue a déjà été choisie dans la landing (sélecteur
        // en haut) → on enchaîne directement sur l'écran de choix (auth). La page
        // Legal dédiée a été retirée : le consentement cookies passe par le
        // CookieBanner (affiché à tous les visiteurs) et les CGU sont acceptées
        // à l'inscription.
        try { const l = localStorage.getItem("hc_lang"); if (l) setLang(l); } catch(e){}
        // Navigation libre : "Rejoindre l'aventure" entre DIRECT dans l'app en
        // invité. L'inscription est demandée seulement au moment d'agir
        // (contacter / publier / favori...) via SignupPrompt. Se connecter reste
        // accessible depuis le profil (invité -> prompt "Se connecter").
        setUser(null);
        setScreen("dash");
      }
    };
    return (
      <>
        <G/>
        {toast && <Toast msg={toast}/>}
        {showCookieBanner && (
          <CookieBanner lang={lang} onClose={() => setShowCookieBanner(false)}/>
        )}
        {useOldDesign ? (
          <LandingPage showToast={showToast} onEnter={handleEnter} />
        ) : (
          <LandingPageV2 showToast={showToast} onEnter={handleEnter} />
        )}
      </>
    );
  }

  return (
    <div style={{maxWidth:430,margin:"0 auto",minHeight:"100vh",position:"relative",fontFamily:"var(--fb)"}}>
      <G/>
      {toast && <Toast msg={toast}/>}
      {exitConfirm && (() => {
        const e = EXIT_T[lang] || EXIT_T.fr;
        return (
          <div onClick={()=>setExitConfirm(false)} style={{
            position:"fixed", inset:0, zIndex:3000, background:"rgba(0,0,0,.6)",
            display:"flex", alignItems:"center", justifyContent:"center", padding:22,
          }}>
            {/* Fond OPAQUE obligatoire : var(--hc-surface) est semi-transparent
                en Nocturne et laisserait voir la page au travers. */}
            <div onClick={ev=>ev.stopPropagation()} style={{
              width:"100%", maxWidth:340, background:"var(--hc-overlay)", color:"var(--hc-text)",
              borderRadius:18, padding:"22px 20px 16px", border:"1px solid var(--hc-border)",
              boxShadow:"0 20px 50px rgba(0,0,0,.5)", textAlign:"center",
            }}>
              <div style={{fontSize:17, fontWeight:900, fontFamily:"var(--fh)", marginBottom:8}}>{e.title}</div>
              <div style={{fontSize:13.5, color:"var(--hc-text-muted)", lineHeight:1.6, marginBottom:20}}>{e.body}</div>
              {/* Le choix sûr est à DROITE et en plein : le pouce y tombe, et le
                  regard finit sa lecture de ce côté. Quitter reste discret. */}
              <div style={{display:"flex", gap:10}}>
                <button onClick={doExit} style={{
                  flex:1, padding:"12px 10px", borderRadius:12, cursor:"pointer",
                  background:"transparent", color:"var(--hc-text-muted)",
                  border:"1px solid var(--hc-border)", fontSize:14, fontWeight:700, fontFamily:"var(--fh)",
                }}>{e.quit}</button>
                <button onClick={()=>setExitConfirm(false)} style={{
                  flex:1.4, padding:"12px 10px", borderRadius:12, border:"none", cursor:"pointer",
                  background:"#C8001A", color:"#fff", fontSize:14.5, fontWeight:800, fontFamily:"var(--fh)",
                  boxShadow:"0 6px 18px rgba(200,0,26,.4)",
                }}>{e.stay}</button>
              </div>
            </div>
          </div>
        );
      })()}
      {showInstallPopup && (
        <InstallAppPopup
          lang={lang}
          onClose={() => setShowInstallPopup(false)}
          onPermanentClose={() => {
            try { localStorage.setItem('hc_install_popup_dismissed', 'true'); } catch(e){}
            setShowInstallPopup(false);
          }}
        />
      )}
      {showCookieBanner && (
        <CookieBanner lang={lang} onClose={() => setShowCookieBanner(false)}/>
      )}
      {showEvolved && (
        <EvolvedNotice
          lang={lang}
          onUpdate={() => {
            try { if (user?.id) localStorage.setItem("hc_evolved_seen_" + user.id, "1"); } catch(e){}
            setShowEvolved(false);
            navigate("profileEdit");
          }}
          onDismiss={() => {
            try { if (user?.id) localStorage.setItem("hc_evolved_seen_" + user.id, "1"); } catch(e){}
            setShowEvolved(false);
          }}
        />
      )}
      {founderPreview && (
        <FounderNotice
          lang={lang}
          memberNo={founderPreview.memberNo}
          variant={founderPreview.variant}
          onClose={() => setFounderPreview(null)}
        />
      )}
      {showFounder && !showEvolved && !founderPreview && (
        <FounderNotice
          lang={lang}
          memberNo={user?.memberNo}
          variant={
            user?.createdAt && new Date(user.createdAt) < FOUNDER_OFFER_LAUNCH
              ? "existing" : "new"
          }
          onClose={() => {
            try { if (user?.id) localStorage.setItem("hc_founder_seen_" + user.id, "1"); } catch(e){}
            setShowFounder(false);
          }}
        />
      )}
      {showCreateFiche && !showEvolved && !showFounder && (
        <CreateFicheNotice
          lang={lang}
          onCreate={() => {
            try { if (user?.id) localStorage.setItem("hc_fiche_nudge_" + user.id, "1"); } catch(e){}
            setShowCreateFiche(false);
            navigate("proFicheEdit", { pro: null });
          }}
          onDismiss={() => {
            try { if (user?.id) localStorage.setItem("hc_fiche_nudge_" + user.id, "1"); } catch(e){}
            setShowCreateFiche(false);
          }}
        />
      )}
      {profileNudge && (() => {
        const PN = ({
          fr:{ title:"Bienvenue sur HorseConnex ! 🐴", desc:"Complète ton profil en 1 minute pour publier des annonces, contacter des vendeurs et bâtir ta réputation cavalière.", cta:"Compléter mon profil", later:"Plus tard" },
          en:{ title:"Welcome to HorseConnex! 🐴", desc:"Complete your profile in 1 minute to post listings, contact sellers and build your rider reputation.", cta:"Complete my profile", later:"Later" },
          de:{ title:"Willkommen bei HorseConnex! 🐴", desc:"Vervollständige dein Profil in 1 Minute, um Anzeigen zu schalten, Verkäufer zu kontaktieren und deinen Ruf aufzubauen.", cta:"Profil vervollständigen", later:"Später" },
          es:{ title:"¡Bienvenido a HorseConnex! 🐴", desc:"Completa tu perfil en 1 minuto para publicar anuncios, contactar vendedores y crear tu reputación.", cta:"Completar mi perfil", later:"Más tarde" },
          it:{ title:"Benvenuto su HorseConnex! 🐴", desc:"Completa il tuo profilo in 1 minuto per pubblicare annunci, contattare venditori e costruire la tua reputazione.", cta:"Completa il profilo", later:"Più tardi" },
          pt:{ title:"Bem-vindo ao HorseConnex! 🐴", desc:"Completa o teu perfil em 1 minuto para publicar anúncios, contactar vendedores e criar a tua reputação.", cta:"Completar o meu perfil", later:"Mais tarde" },
          nl:{ title:"Welkom bij HorseConnex! 🐴", desc:"Vervolledig je profiel in 1 minuut om advertenties te plaatsen, verkopers te contacteren en je reputatie op te bouwen.", cta:"Mijn profiel vervolledigen", later:"Later" },
        })[lang] || {};
        return (
          <div onClick={()=>{ noterProfilRefuse(user?.id || authSession?.user?.id); setProfileNudge(false); }} style={{position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,.6)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:18,padding:"26px 22px",maxWidth:360,width:"100%",textAlign:"center",boxShadow:"0 20px 60px rgba(0,0,0,.45)"}}>
              <div style={{fontSize:44,marginBottom:8}}>🐴</div>
              <div style={{fontSize:19,fontWeight:800,color:"#1A1A2E",fontFamily:"var(--fh)",marginBottom:8}}>{PN.title}</div>
              <div style={{fontSize:14,color:"#5a5a6e",lineHeight:1.55,marginBottom:20,padding:"0 4px"}}>{PN.desc}</div>
              <button onClick={()=>{ noterProfilRefuse(user?.id || authSession?.user?.id); setProfileNudge(false); navigate("onboarding"); }} style={{background:"var(--red)",color:"#fff",border:"none",borderRadius:12,padding:"14px 20px",fontSize:15.5,fontFamily:"var(--fh)",fontWeight:700,cursor:"pointer",width:"100%",marginBottom:10}}>{PN.cta}</button>
              <button onClick={()=>{ noterProfilRefuse(user?.id || authSession?.user?.id); setProfileNudge(false); }} style={{background:"transparent",color:"#7A7A8C",border:"none",padding:"8px",fontSize:14,fontFamily:"var(--fh)",fontWeight:600,cursor:"pointer",width:"100%"}}>{PN.later}</button>
            </div>
          </div>
        );
      })()}

      <Suspense fallback={<LazyFallback />}>
      {screen==="splash"       && (useOldDesign
        ? <SplashScreen onDone={()=>navigate("auth")} lang={lang}/>
        : <SplashScreenV2 onDone={()=>navigate("auth")}/>)}
      {screen==="auth"         && <AuthChoice      onFirst={()=>navigate("register")} onLogin={()=>navigate("login")} onGuest={()=>{setUser(null);navigate("dash");}} lang={lang}/>}
      {screen==="login"        && <LoginScreen     onLogin={(isAdmin)=>navigate(isAdmin?"admin":"dash")} onBack={goBack} showToast={showToast} setUserCats={setUserCats} navigate={navigate} lang={lang}/>}
      {screen==="register"     && <RegisterScreen  onRegister={(cats)=>finirInscription(cats)} onBack={goBack} showToast={showToast} lang={lang} setUser={setUser} navigate={navigate}/>}
      {screen==="onboarding"   && <RegisterScreen  completeProfile googleUser={authSession?.user} onRegister={(cats)=>finirInscription(withAdminTag(cats, authSession?.user?.email))} onBack={goBack} showToast={showToast} lang={lang} setUser={setUser} navigate={navigate}/>}
      {screen==="dash"         && <Dashboard       navigate={navigate} showToast={showToast} userCats={userCats} lang={lang} user={user} setUser={setUser} initialTab={params.tab} onTabChange={(t)=>setParams(p=>({...p,tab:t}))}/>}
      {screen==="map"          && <MapScreen       ads={params.ads||[]} mode={params.mode||"all"} focus={params.focus||null} onBack={goBack} lang={lang} user={user} navigate={navigate}/>}
      {screen==="adDetail"     && <AdDetailScreen  ad={params.ad} onBack={goBack} showToast={showToast} navigate={navigate} lang={lang} user={user} userLoaded={userLoaded}/>}
      {screen==="unsubscribed" && <UnsubscribedScreen ok={params.ok} proId={params.proId} unsubKey={params.unsubKey} lang={lang} onHome={()=>navigate("dash",{tab:"pros"})}/>}
      {screen==="proDetail"    && <ProDetailScreen pro={params.pro} proId={params.proId} claimKey={params.claimKey} onBack={goBack} lang={lang} user={user} showToast={showToast} navigate={navigate} onClaimed={(pro)=>navigate("proFicheEdit",{pro})} onEdit={(pro)=>navigate("proFicheEdit",{pro})} onDeleted={()=>navigate("dash",{tab:"pros"})}/>}
      {screen==="proFicheEdit" && <ProFicheEditScreen existing={params.pro||null} user={user} onBack={goBack} onSaved={()=>navigate("dash",{tab:"pros"})} onOpenPro={(pro)=>navigate("proFicheEdit",{pro})} showToast={showToast} lang={lang}/>}
      {screen==="marketDetail" && <MarketDetailScreen item={params.item} onBack={goBack} showToast={showToast} navigate={navigate} user={user} lang={lang}/>}
      {screen==="publish"      && <PublishScreen   onBack={goBack} showToast={showToast} userCats={params.userCats||[]} editAd={params.editAd} user={user} presetCat={params.presetCat} prefill={params.prefill} lang={lang}/>}
      {screen==="chat"         && <ChatScreen      convo={params.convo} onBack={goBack} showToast={showToast} navigate={navigate} lang={lang}/>}
      {screen==="trails"       && <TabTrails       navigate={navigate} onBack={goBack} showToast={showToast} user={user} lang={lang} isGuest={!user?.id} requireAccount={()=>navigate("register")}/>}
      {screen==="trailDetail"  && <TrailDetailScreen trail={params.trail} onBack={goBack} navigate={navigate} showToast={showToast} user={user} lang={lang}/>}
      {screen==="publishTrail" && <PublishTrailScreen onBack={goBack} showToast={showToast} user={user} lang={lang} prefill={params.prefill}/>}
      {screen==="recordTrail"  && <RecordTrailScreen onBack={goBack} navigate={navigate} showToast={showToast} user={user} lang={lang}/>}
      {screen==="addPoi"       && <AddPoiScreen onBack={goBack} showToast={showToast} user={user} lang={lang} center={params.center}/>}
      {screen==="profil"       && <ProfilPage      navigate={navigate} showToast={showToast} userCats={userCats} user={user} setUser={setUser} lang={lang}/>}
      {screen==="profileEdit"  && <ProfileEditScreen onBack={goBack} showToast={showToast} userCats={userCats} setUserCats={setUserCats} user={user} setUser={setUser} lang={lang}/>}
      {screen==="myAds"        && <MyAdsScreen     onAd={ad=>navigate("adDetail",{ad})} onBack={goBack} onPublish={()=>navigate("publish",{userCats})} onEdit={(ad)=>navigate("publish",{editAd:ad})} lang={lang}/>}
      {screen==="carnet"       && <CarnetScreen    onBack={goBack} navigate={navigate} showToast={showToast} user={user} lang={lang}/>}
      {screen==="favorites"    && <FavoritesScreen onAd={ad=>navigate("adDetail",{ad})} onPro={pro=>navigate("proDetail",{proId:pro.id})} onBack={goBack} lang={lang}/>}
      {screen==="notifications"&& <NotificationsScreen onBack={goBack} navigate={navigate} lang={lang}/>}
      {screen==="security"     && <SecurityScreen  onBack={goBack} showToast={showToast} lang={lang}/>}
      {screen==="help"         && <HelpScreen      onBack={goBack} showToast={showToast} navigate={navigate} lang={lang}/>}
      {screen==="about"        && <AboutScreen     onBack={goBack} navigate={navigate} lang={lang}/>}
      {screen==="langSettings" && <LangSettingsScreen onBack={goBack} lang={lang} setLang={setLang} user={user} showToast={showToast}/>}
      {screen==="agenda"         && <AgendaScreen        onBack={goBack} showToast={showToast} navigate={navigate} user={user} lang={lang}/>}
      {screen==="forgotPassword" && <ForgotPasswordScreen onBack={goBack} showToast={showToast} lang={lang}/>}
      {screen==="contactSAV"     && <ContactSAVScreen     onBack={goBack} showToast={showToast} lang={lang}/>}
      {screen==="premium"        && <PremiumScreen       onBack={goBack} showToast={showToast} user={user} lang={lang}/>}
      {screen==="myReputation"   && <MyReputationScreen   onBack={goBack} showToast={showToast} lang={lang}/>}
      {screen==="riderRep"       && <RiderReputationScreen rider={params.rider} onBack={goBack} showToast={showToast} lang={lang}/>}
      {screen==="sellerProfile"  && <SellerProfileScreen sellerId={params.sellerId} onBack={goBack} onAd={ad=>navigate("adDetail",{ad})} navigate={navigate} showToast={showToast} lang={lang} user={user}/>}
      {screen==="horseroad"        && <TabHorseRoad     navigate={navigate} showToast={showToast} user={user} t={T[lang]} lang={lang}/>}
      {screen==="publishHorseRoad" && <PublishHorseRoad navigate={navigate} showToast={showToast} user={user} t={T[lang]} lang={lang} presetType={params.preset_type||"offer"} editTrip={params.edit_trip||null}/>}
      {screen==="horseroadDetail"  && <HorseRoadDetail  trip={params.trip} navigate={navigate} showToast={showToast} user={user} t={T[lang]} lang={lang}/>}
      {screen==="horseroadMap"     && <HorseRoadMapScreen trips={params.trips||[]} onBack={goBack} navigate={navigate} lang={lang}/>}
      {screen==="admin"          && <AdminPanel      onBack={()=>navigate("dash")} showToast={showToast} navigate={navigate}/>}
      {screen==="logout"         && (()=>{
        supabase.auth.signOut();
        setUser({prenom:"",nom:"",email:"",tel:"",ville:"",pays:"",bio:"",avatar:null,badge:null,verified:false,cats:[],showTel:false,showAdresse:false});
        setUserCats([]);
        navigate("auth");
        return null;
      })()}
      </Suspense>
    </div>
  );
}
